import { clearTermsDraft } from "./termsStorage";

const ENTRY_INTENT_KEY = "sonicbox-entry-intent-v1";
const RADIO_DESTINATION_KEY = "sonicbox-radio-destination-v1";
const REDIRECT_LOGIN_KEY = "redirectLogin";
const INTENT_TTL_MS = 24 * 60 * 60 * 1000;
const volatileValues = new Map();

const isBrowser = () => typeof window !== "undefined";

const readJson = (key, fallback = null) => {
  if (!isBrowser()) return fallback;

  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch (error) {
    return volatileValues.get(key) ?? fallback;
  }
};

const writeJson = (key, value) => {
  if (!isBrowser()) return;

  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    volatileValues.set(key, value);
  }
};

const createIntentId = () => {
  if (isBrowser() && window.crypto?.randomUUID) {
    return `entry_${window.crypto.randomUUID()}`;
  }

  return `entry_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const now = () => Date.now();

export const buildEntryIntent = ({
  source,
  originalPath,
  nameseo = null,
  encodedCode = null,
  radio = null,
}) => {
  const createdAt = now();

  return {
    version: 1,
    id: createIntentId(),
    source,
    originalPath,
    target: {
      nameseo: nameseo ?? radio?.nameseo ?? null,
      radioid: radio?.radioid ?? radio?.id ?? null,
      radioname: radio?.radioname ?? radio?.name ?? null,
    },
    redeem: {
      encodedCode,
      code: null,
      status: encodedCode ? "pending" : "none",
    },
    task: {
      required: null,
      status: "unknown",
    },
    terms: {
      required: null,
      status: "unknown",
    },
    auth: {
      required: true,
      status: "unknown",
    },
    createdAt,
    updatedAt: createdAt,
    expiresAt: createdAt + INTENT_TTL_MS,
  };
};

export const getEntryIntent = () => {
  const intent = readJson(ENTRY_INTENT_KEY, null);
  if (!intent) return null;

  if (intent.expiresAt && intent.expiresAt <= now()) {
    // Expiry invalidates acceptance/auth state, never the radio or pending code.
    clearTermsDraft();
    return saveEntryIntent(rebuildEntryIntent(intent));
  }

  return intent;
};

export const hasActiveEntryIntent = () => !!getEntryIntent();

export const saveEntryIntent = (intent) => {
  if (!intent) return null;

  const nextIntent = {
    ...intent,
    updatedAt: now(),
    expiresAt: intent.expiresAt ?? now() + INTENT_TTL_MS,
  };

  writeJson(ENTRY_INTENT_KEY, nextIntent);
  const destination =
    getEntryIntentTargetPath(nextIntent) ||
    getEntryIntentResumePath(nextIntent);
  if (destination) writeJson(RADIO_DESTINATION_KEY, { path: destination });
  return nextIntent;
};

export const patchEntryIntent = (patch) => {
  const current = getEntryIntent();
  if (!current) return null;

  return saveEntryIntent({
    ...current,
    ...patch,
    target: {
      ...(current.target ?? {}),
      ...(patch?.target ?? {}),
    },
    redeem: {
      ...(current.redeem ?? {}),
      ...(patch?.redeem ?? {}),
    },
    task: {
      ...(current.task ?? {}),
      ...(patch?.task ?? {}),
    },
    terms: {
      ...(current.terms ?? {}),
      ...(patch?.terms ?? {}),
    },
    auth: {
      ...(current.auth ?? {}),
      ...(patch?.auth ?? {}),
    },
  });
};

export const clearEntryIntent = () => {
  if (!isBrowser()) return;
  clearTermsDraft();
  volatileValues.delete(ENTRY_INTENT_KEY);
  try {
    window.sessionStorage.removeItem(ENTRY_INTENT_KEY);
  } catch (_) {
    /* Storage may be disabled. */
  }
  try {
    window.localStorage.removeItem(ENTRY_INTENT_KEY);
  } catch (_) {
    /* Storage may be disabled. */
  }
};

export const getLegacyRedirectLogin = () => {
  if (!isBrowser()) return null;
  try {
    return (
      window.sessionStorage.getItem(REDIRECT_LOGIN_KEY) ??
      window.localStorage.getItem(REDIRECT_LOGIN_KEY)
    );
  } catch (_) {
    return null;
  }
};

export const setLegacyRedirectLogin = (path) => {
  if (!isBrowser() || !path) return;
  try {
    window.sessionStorage.setItem(REDIRECT_LOGIN_KEY, path);
  } catch (_) {
    /* Storage may be disabled. */
  }
  try {
    window.localStorage.setItem(REDIRECT_LOGIN_KEY, path);
  } catch (_) {
    /* Storage may be disabled. */
  }
};

export const clearLegacyRedirectLogin = () => {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.removeItem(REDIRECT_LOGIN_KEY);
  } catch (_) {
    /* Storage may be disabled. */
  }
  try {
    window.localStorage.removeItem(REDIRECT_LOGIN_KEY);
  } catch (_) {
    /* Storage may be disabled. */
  }
};

export const getEntryIntentTargetPath = (intent = getEntryIntent()) => {
  const nameseo = intent?.target?.nameseo;
  return nameseo ? `/sonicbox/${nameseo}` : null;
};

export const getEntryIntentResumePath = (intent = getEntryIntent()) => {
  if (!intent) return null;

  if (intent?.redeem?.encodedCode && intent?.redeem?.status === "pending") {
    return `/redeem/${intent.redeem.encodedCode}`;
  }

  const nameseo = intent?.target?.nameseo;
  if (!nameseo) return null;

  if (intent.source === "task") {
    return `/task/${nameseo}`;
  }

  return `/sonicbox/${nameseo}`;
};

export const getLoginResumePath = (fallback = "/sonicboxes") => {
  const intentResumePath = getEntryIntentResumePath();
  if (intentResumePath) return intentResumePath;

  const radioDestination = getRadioDestinationPath();
  if (radioDestination) return radioDestination;

  const legacyRedirect = getLegacyRedirectLogin();
  if (legacyRedirect) return legacyRedirect;

  return fallback;
};

export const prepareEntryIntentForCurrentLocation = () => {
  if (!isBrowser()) return null;

  const originalPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const sonicboxMatch = window.location.pathname.match(
    /^\/sonicbox\/([^/?#]+)/,
  );
  const taskMatch = window.location.pathname.match(/^\/task\/([^/?#]+)/);
  const redeemMatch = window.location.pathname.match(/^\/redeem\/([^/?#]+)/);

  let intent = null;

  if (sonicboxMatch) {
    intent = buildEntryIntent({
      source: "sonicbox",
      originalPath,
      nameseo: sonicboxMatch[1],
    });
  } else if (taskMatch) {
    intent = buildEntryIntent({
      source: "task",
      originalPath,
      nameseo: taskMatch[1],
    });
  } else if (redeemMatch) {
    intent = buildEntryIntent({
      source: "redeem",
      originalPath,
      encodedCode:
        redeemMatch[1] === "code"
          ? new URLSearchParams(window.location.search).get("c")
          : decodeURIComponent(redeemMatch[1]),
    });
  }

  if (!intent) {
    return getEntryIntent();
  }

  const existing = getEntryIntent();
  if (existing && isSameEntryTarget(existing, intent)) {
    setLegacyRedirectLogin(getEntryIntentResumePath(existing));
    return existing;
  }
  clearTermsDraft();
  saveEntryIntent(intent);
  const resumePath = getEntryIntentResumePath(intent);
  if (resumePath) {
    setLegacyRedirectLogin(resumePath);
  }

  return intent;
};

export const migrateLegacyRedirectIntent = () => {
  const redirectLogin = getLegacyRedirectLogin();
  if (!redirectLogin) return null;

  const match = redirectLogin.match(/^\/sonicbox\/([^/?#]+)/);
  if (!match) return null;

  const intent = buildEntryIntent({
    source: "legacy_redirect",
    originalPath: redirectLogin,
    nameseo: match[1],
  });

  saveEntryIntent(intent);
  return intent;
};

export function isSameEntryTarget(current, next) {
  if (!current || !next) return false;
  if (current.source !== next.source) return false;
  if (next.source === "redeem")
    return current.redeem?.encodedCode === next.redeem?.encodedCode;
  return current.target?.nameseo === next.target?.nameseo;
}

// A destination is a per-tab navigation context, not evidence of acceptance.
// It outlives the 24-hour draft and a completed or cancelled authentication.
export function getRadioDestinationPath() {
  return readJson(RADIO_DESTINATION_KEY)?.path || null;
}

export function getRadioEntryPath() {
  return getEntryIntentResumePath() || getRadioDestinationPath();
}

function rebuildEntryIntent(intent) {
  const next = buildEntryIntent({
    source: intent.source,
    originalPath: intent.originalPath,
    nameseo: intent.target?.nameseo,
    radio: intent.target,
    encodedCode:
      intent.redeem?.status === "pending" ? intent.redeem.encodedCode : null,
  });
  return next;
}

export function restartRadioEntry() {
  const existing = getEntryIntent();
  clearTermsDraft();
  let next;
  if (existing) next = rebuildEntryIntent(existing);
  else {
    const path = getRadioDestinationPath();
    const match = path?.match(/^\/sonicbox\/([^/?#]+)/);
    if (match)
      next = buildEntryIntent({
        source: "sonicbox",
        originalPath: path,
        nameseo: match[1],
      });
  }
  if (next) {
    saveEntryIntent(next);
    const path = getEntryIntentResumePath(next);
    setLegacyRedirectLogin(path);
    return path;
  }
  return getRadioDestinationPath();
}
